import React, { FC, useEffect, useMemo } from 'react';

import {
  Box,
  Flex,
  Modal,
  Input,
  Divider,
  Button,
  ModalBody,
  IconButton,
  ModalHeader,
  ModalContent,
  ModalOverlay,
  ModalCloseButton,
} from '@chakra-ui/react';

import { Form, Formik, useFormikContext } from 'formik';
import { FormControl, FormLabel } from '@chakra-ui/form-control';

import { HiMinusCircle } from 'react-icons/hi';

import { useSKU } from 'hooks/useSKU';

import FormikInput from 'components/primitives/FormikInput';
import FormikTextArea from 'components/primitives/FormikTextArea';
import FormikSelectInput from 'components/primitives/FormikSelectInput';

import { validationSchema } from './AddOrderModal.utils';

import { AddOrderModalViewProps } from './AddOrderModal.props';
import getSortedData from 'utils/getSortedData';
import FormikDatepicker from 'components/primitives/FormikDatepicker';

import { Order } from 'types/order';
import { SiteFilterableSelect } from '../SiteFilterableSelect';
import { calculateItemsPerPackage } from 'utils/itemsHelper';
import { SkuSelect } from 'components/primitives/SkuSelect';

const labelProps = {
  fontSize: '12px',
  color: '#afafaf',
};

const DayOfTheWeekMap: { [key: string]: string } = {
  Monday: 'Mon',
  Tuesday: 'Tue',
  Wednesday: 'Wed',
  Thursday: 'Thu',
  Friday: 'Fri',
  Saturday: 'Sat',
  Sunday: 'Sun',
};

const AddOrderModalView: FC<AddOrderModalViewProps> = (props) => {
  const {
    sites,
    isOpen,
    onClose,
    onSubmit,
    isLoading,
    isLoadingSites,
    selectedDay,
  } = props;

  const initialValues = {
    site: '',
    deliveryAt: selectedDay ? selectedDay : '',
    details: [
      {
        casesCount: '0',
        sku: '',
      },
    ],
    dayOfWeek: 0,
    notes: '',
  };

  const { data: skuData } = useSKU('package');

  return (
    <Modal
      size="6xl"
      isCentered
      isOpen={isOpen}
      onClose={onClose}
      scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent borderRadius="10px" px={5}>
        <ModalCloseButton _focus={{ outline: 'none' }} isDisabled={isLoading} />
        <ModalHeader mt={5} textAlign="center">
          Create new order
        </ModalHeader>
        <ModalBody>
          <Flex direction="column" p={6}>
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={onSubmit}>
              {({ values, isValid, setFieldValue }) => {
                const _site: any = sites?.find((s) => s._id === values.site);
                const isDaily = _site?.deliveryFrequency === 'Daily';
                const _deliveryDayOfTheWeek = _site?.deliveryDayOfTheWeek;

                let deliveryDayOfTheWeek = '';

                if (isDaily) {
                  deliveryDayOfTheWeek = '';
                } else if (Array.isArray(_deliveryDayOfTheWeek)) {
                  if (_deliveryDayOfTheWeek?.length === 1) {
                    deliveryDayOfTheWeek = _deliveryDayOfTheWeek?.[0];
                  } else {
                    deliveryDayOfTheWeek = _deliveryDayOfTheWeek
                      ?.map((day) => DayOfTheWeekMap[day])
                      ?.join(', ');
                  }
                }

                const frequency = _site?.deliveryFrequency;

                let deliveryFrequency = frequency ? `(${frequency})` : '';

                if (isDaily) {
                  deliveryFrequency = frequency;
                }

                return (
                  <Form>
                    <Flex alignItems="flex-end">
                      <SiteFilterableSelect
                        values={sites}
                        containerProps={{ flex: 1 }}
                        label="Site(*)"
                        name="site"
                        labelProps={labelProps}
                        isRequired={false}
                      />

                      <Box h="20px" w="20px" />
                      <FormControl style={{ flex: 1 }}>
                        <FormLabel
                          htmlFor={'deliveryDayOfTheWeek'}
                          fontSize="12px"
                          color="#afafaf"
                          fontWeight="bold"
                          textTransform="capitalize">
                          Delivery Day of the week
                        </FormLabel>
                        <FormikInput
                          name="deliveryDayOfTheWeek"
                          value={
                            `${deliveryDayOfTheWeek} ${deliveryFrequency}` ||
                            '0'
                          }
                          isReadOnly
                        />
                      </FormControl>

                      <Box h="20px" w="20px" />
                      <FormikDatepicker
                        name={'deliveryAt'}
                        label="Delivery Date(*)"
                        containerProps={{ flex: 1 }}
                        labelProps={labelProps}
                      />
                      <Box h="82px" />
                    </Flex>
                    <Flex justify="space-between" gap={4}>
                      <Box mt="8" w="100%">
                        <FormikTextArea
                          name="notes"
                          label="Customer Order Notes"
                          value={values.notes}
                          description="Can also include notes to drivers (such as equipment) as this is visible to driver in app during delivery."
                        />
                      </Box>
                      <Box mt="8" w="100%">
                        <FormikTextArea
                          name="notes"
                          label="Site Delivery instructions"
                          description="Visible every delivery in app. Edit in customer details."
                          value={_site?.deliveryInstructions}
                          bg="red"
                          isReadOnly
                        />
                      </Box>
                    </Flex>

                    {values.details.map((d, i) => {
                      const _sku = skuData?.find((s) => s._id === d.sku);
                      const itemsPerPackage = calculateItemsPerPackage(_sku);
                      return (
                        <Flex
                          key={`${d.sku}-${d.sku}-${i}`}
                          mt={6}
                          position="relative"
                          alignItems="flex-end">
                          <PackageSiteSelect index={i} siteId={values.site} />
                          <Box h="20px" w="20px" />
                          <FormikInput
                            id={`count-${i}`}
                            type="number"
                            name={`details[${i}].casesCount`}
                            label="Package Amount(*)"
                            labelProps={labelProps}
                            containerProps={{ flex: 1 }}
                          />
                          <Box h="20px" w="20px" />
                          <FormikInput
                            name="itemsPerPackage"
                            label="Items per Package"
                            labelProps={labelProps}
                            containerProps={{ flex: 1 }}
                            isReadOnly
                            value={itemsPerPackage}
                          />
                          <Box h="20px" w="20px" />
                          <TotalItemsInput i={i} />
                          {values.details.length > 1 && (
                            <IconButton
                              aria-label="remove-item"
                              icon={
                                <HiMinusCircle size="25px" fill="#E54444" />
                              }
                              size="sm"
                              variant="ghost"
                              position="absolute"
                              right={-10}
                              mb={1}
                              onClick={() =>
                                setFieldValue('details', [
                                  ...values?.details?.filter(
                                    (_, index) => index !== i,
                                  ),
                                ])
                              }
                            />
                          )}
                        </Flex>
                      );
                    })}
                    <Flex flex={1} mt={4} w="100%">
                      {values?.details?.length <= 9 && (
                        <Button
                          w="100%"
                          size="lg"
                          borderRadius="5px"
                          variant="outline"
                          bg="primary.900"
                          onClick={() =>
                            setFieldValue('details', [
                              ...values.details,
                              {
                                sku: '',
                                casesCount: '',
                              },
                            ])
                          }>
                          Add SKU (max 10)
                        </Button>
                      )}
                    </Flex>
                    <Flex my={12} flexDirection="row" justifyContent="center">
                      <Button
                        w="134px"
                        onClick={onClose}
                        isDisabled={isLoading}
                        variant="outline">
                        Cancel
                      </Button>
                      <Divider w="10px" />
                      <Button
                        type="submit"
                        w="134px"
                        isLoading={isLoading}
                        isDisabled={!isValid}>
                        Create
                      </Button>
                    </Flex>
                  </Form>
                );
              }}
            </Formik>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default AddOrderModalView;

const TotalItemsInput: FC<{ i: number }> = ({ i }) => {
  const { data } = useSKU('package');
  const { values, setFieldValue } = useFormikContext<Order>();

  const skuSize = useMemo(() => {
    const item = values?.details?.[i];
    const _sku = data?.find((s) => s._id === item?.sku);
    const totalQuantity = ((_sku as any)?.items as any[])
      ?.map((i) => i?.quantity)
      ?.reduce?.((acc, current) => acc + current, 0);

    return Number(item.casesCount || '0') * Number(totalQuantity || '0');
  }, [JSON.stringify(values?.details?.[i])]);

  useEffect(() => {
    setFieldValue(`details[${i}].totalItems`, skuSize);
  }, [skuSize]);

  return (
    <FormikInput
      type="number"
      id={`item-${i}`}
      label="Total items"
      labelProps={labelProps}
      containerProps={{ flex: 1 }}
      name={`details[${i}].totalItems`}
      isReadOnly
    />
  );
};

const PackageSiteSelect: FC<{ index: number; siteId: string }> = ({
  index,
  siteId,
}) => {
  const { isLoading, data: sku = [] } = useSKU('package', undefined, siteId);

  return (
    <FormikSelectInput
      id={`sku-${index}`}
      placeholder="Select SKU"
      label="Package SKU(*)"
      isLoading={isLoading}
      containerProps={{ flex: 1 }}
      name={`details[${index}].sku`}
      labelProps={labelProps}>
      {getSortedData(sku)?.map((p) => (
        <option key={p._id} value={p._id}>
          {p?.name || ''}
        </option>
      ))}
    </FormikSelectInput>
  );
};
